import _, {concat, noop} from 'lodash';
import React, {Component, ReactNode} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {EventHandler, FrontEvent} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {TagBehaviorsEnum} from '../../../../../libs/shared/core/src/models/api/commonTagModel';
import {CompanyModel} from '../../../../../libs/shared/core/src/models/api/company/companyModel';
import {TeamModel} from '../../../../../libs/shared/core/src/models/api/teamModel';
import {fetchApiAsync} from '../../../../../libs/shared/core/src/services/http/http';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {ResourceMap} from '../../../../../libs/shared/core/src/store/commonAppTypes';
import {TeamAvatar} from '../../../../../libs/shared/ui/src/components/avatar/teamAvatar';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {InlineLinkButton} from '../../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownCategory} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownCategory';
import {DropdownEmpty} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownEmpty';
import {DropdownVirtualList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownVirtualList';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemCheckbox} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemCheckbox';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {
  AsyncSearchDropdown,
  AsyncSearchDropdownDataFetcher,
} from '../../../../../libs/shared/ui/src/components/dropdowns/search/asyncSearchDropdown';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {center, ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {compareName} from '../../../../../srcCommon/helpers/localeHelpers';
import {buildTagSearchUrl, sortTags, tagHasBehaviors} from '../../../../helpers/models/tagHelpers';
import {ApiSearchTags, SearchTagModel} from '../../../../models/api/searchTagModel';
import {TagModel} from '../../../../models/api/tagModel';
import {DropdownItemAccentIcon} from '../../../common/dropdowns/dropdownItemAccentIcon';
import {TagDropdownItem} from '../../../common/dropdowns/tagDropdownItem';
import {CreateWorkspaceTagDropdownContainer} from '../../../common/dropdowns/tags/createWorkspaceTagDialogContainer';
import {workspacesEditorDropdownHeight} from '../editor/context/types';
import {TagCreatedHandler} from '../editor/context/workspacesEditorContext';

/*
 * Props.
 */

export type ToggleTagHandler = (searchTag: SearchTagModel) => void;

interface PickTagsDropdownProps extends DropdownProps {
  company: CompanyModel;
  team?: TeamModel;
  behaviors?: ReadonlyArray<TagBehaviorsEnum>;
  namespace: string;
  visibleCustomViewIds: ReadonlyArray<number>;
  toggleTag: ToggleTagHandler;
  titleLabel?: ReactNode;
  backButtonLabel: ReactNode;
  onBackButtonClick: () => void;
  shouldDisplayAddButton: boolean;
  onCreateTagSelect?: (e: FrontEvent, initialName: string) => void;
  tags: ResourceMap<TagModel>;
  isEditingWorkspace?: boolean;
  onTagCreated?: TagCreatedHandler;
  tagDropdownSelectedIds?: ReadonlyArray<TagModel['id']>;
}

interface PickTagsDropdownPaginationData {
  offset: number;
}

/*
 * Constants.
 */

const visibleCustomViewIdsMaxLength = 500;
const tagsSearchPaginationLimit = 50;

/*
 * Style.
 */

const StyledHeaderDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledTeamNameDiv = styled.div`
  margin-left: 5px;

  ${ellipsis()};
`;

const StyledInlineLinkButton = styled(InlineLinkButton)`
  ${center()};
`;

const StyledInlineLinkText = styled.div`
  padding-top: 20px;
  font-weight: ${fontWeights.semibold};
  max-width: 200px;

  ${ellipsis()};
`;

const StyledEmptyStateText = styled.div`
  ${center()};
`;

/*
 * Component.
 */

enum PickTagsActiveItemsEnum {
  TAGS = 'TAGS',
  CREATE_TAGS = 'CREATE_TAGS',
}

interface PickTagsDropdownState {
  initialTagModels: Array<SearchTagModel>;
  activeItem: PickTagsActiveItemsEnum;
  searchQuery: string;
}

export class PickTagsDropdown extends Component<PickTagsDropdownProps, PickTagsDropdownState> {
  constructor(props: PickTagsDropdownProps) {
    super(props);

    const initialItems = this.getInitialTags();

    this.state = {
      initialTagModels: initialItems,
      activeItem: PickTagsActiveItemsEnum.TAGS,
      searchQuery: '',
    };
  }

  private checkedParentTagIdsOfUncheckedTags: ReadonlyArray<number> = [];

  /**
   * From the passed in props and namespace, construct the initial tags so we can display
   * them during the initial load in case they are from a page source multiple pages in.
   */
  private getInitialTags() {
    const {tags, namespace, behaviors} = this.props;
    return (
      _(tags)
        .values()
        .compact()
        .filter(
          (tag) =>
            tag.namespace === namespace &&
            this.isTagChecked(tag) &&
            // TODO: shouldn't we be using CAN_PIN here, not CAN_DISPLAY?
            tagHasBehaviors(tag, ...(behaviors || [TagBehaviorsEnum.CAN_DISPLAY])),
        )
        // Sorts the initial items alphabetically since they are build from an object which orders based on Id
        //  This constructs the tags as an nested array as that is how the data structures already are.
        .sort((a, b) => compareName(a.name, b.name))
        .map((tag) => {
          // Tags with parents need to be constructed to contain their parents recursively
          const constructedTagArray = [tag];
          let currentParentId: number | undefined = tag.parentTagId;

          // While we have a parent id, we need to append it to the array as they are built as:
          //    [BASE TAG, PARENT1 of BASE, PARENT of PARENT1, etc]
          while (currentParentId) {
            const lookingForParentTagId: number = currentParentId;
            const parentTag: TagModel | undefined = tags[lookingForParentTagId];
            if (parentTag) {
              constructedTagArray.push(parentTag);
            }
            currentParentId = parentTag?.parentTagId;
          }

          return constructedTagArray;
        })
        .value()
    );
  }

  /*
   * Dropdown source.
   */

  private readonly fetchAsync: AsyncSearchDropdownDataFetcher<
    SearchTagModel,
    PickTagsDropdownPaginationData
  > = async (query, tags, cancelToken, paginationData) => {
    const {company, namespace, behaviors, isEditingWorkspace} = this.props;
    const {initialTagModels} = this.state;

    const offset = paginationData?.offset || 0;

    const url = buildTagSearchUrl(company, query, {
      namespaces: [namespace],
      offset,
      behaviors: behaviors || [TagBehaviorsEnum.CAN_DISPLAY],
      specifiedBehaviorsOnly: behaviors !== undefined && behaviors.length > 0,
      limit: tagsSearchPaginationLimit,
      source: 'p1-tags-picker',
    });

    const tagSearch = await fetchApiAsync(ApiSearchTags, url, {cancelToken});

    // Filter out any duplicate tags if we are on the first page (offset of 0) and filter out the main inbox tag
    const shouldIgnoreInitialTagRemoving = Boolean(query) || offset > 0;
    const filteredTags = tagSearch.tags.filter(
      (tag) =>
        (shouldIgnoreInitialTagRemoving || !initialTagModels.some((t) => t[0].id === tag[0].id)) &&
        !tag[0].isMainInboxTag,
    );

    // If we are on the first page, append the initial items and sort the array
    let items = concat(tags, filteredTags);
    if (!query && offset === 0) {
      items = sortTags(concat(items, initialTagModels), (searchTag) => !this.isTagChecked(searchTag[0]));
    }

    const uncheckedTags = items.filter((searchTag) => !this.isTagChecked(searchTag[0]));
    const parentTagsOfUncheckedTags = uncheckedTags.reduce((result: Array<TagModel>, searchTag) => {
      const checkedParentTags = searchTag.filter((tag) => this.isTagChecked(tag));
      if (checkedParentTags) {
        result.push(...checkedParentTags);
      }
      return result;
    }, []);
    const checkedParentTagIdsOfUncheckedTags = _(parentTagsOfUncheckedTags)
      .map((tagModel) => tagModel.id)
      .uniq()
      .value();

    this.checkedParentTagIdsOfUncheckedTags = checkedParentTagIdsOfUncheckedTags;

    // When editing the workspace, we only show items that 1) aren't checked or 2) are parents of unchecked items.
    const editingWorkspaceItems = items.filter(
      (searchTag) =>
        !this.isTagChecked(searchTag[0]) ||
        this.props.tagDropdownSelectedIds?.includes(searchTag[0].id) ||
        this.checkedParentTagIdsOfUncheckedTags.includes(searchTag[0].id),
    );

    const allSearchResultsAreChecked = !editingWorkspaceItems.some(
      (searchTag) => !this.isTagChecked(searchTag[0]),
    );
    const canLoadMore = tagSearch.tags.length === tagSearch.limit;

    // this is the recursive case when all search results are checked
    if (isEditingWorkspace && allSearchResultsAreChecked && canLoadMore) {
      return this.fetchAsync(query, tags, cancelToken, {offset: offset + tagsSearchPaginationLimit});
    }

    // this is the base case in which at least one search result isn't checked
    return {
      data: isEditingWorkspace ? editingWorkspaceItems : items,
      canRequestMore: canLoadMore,
      paginationData: {
        offset: offset + tagsSearchPaginationLimit,
      },
    };
  };

  private isTagChecked(tag: TagModel) {
    return this.props.visibleCustomViewIds.includes(tag.id);
  }

  private isCheckedParentTagOfUncheckedTag(tag: TagModel) {
    return this.checkedParentTagIdsOfUncheckedTags.includes(tag.id);
  }

  private readonly renderRow = (
    searchTag: SearchTagModel,
    index: number,
    _data: ReadonlyArray<SearchTagModel>,
    requestQuery: string,
  ) => {
    const {toggleTag, visibleCustomViewIds, isEditingWorkspace} = this.props;
    const isSearching = Boolean(requestQuery);

    const tag = searchTag[0];
    const trackingCommonProps = {
      tag_id: tag.id,
    };

    const canPinMore = visibleCustomViewIds.length < visibleCustomViewIdsMaxLength;
    const isChecked = this.isTagChecked(tag);

    const onTagSelect: EventHandler = (event) => {
      event.preventDefault(); // Keep the dropdown open
      trackInterfaceEvent(InterfaceEventsEnum.CLICK_TOGGLE_TAG_WORKSPACE, trackingCommonProps);

      toggleTag(searchTag);
    };
    const nestingLevel = isSearching ? 0 : searchTag.length - 1;
    const tagsPath = isSearching ? searchTag : [];

    const isDisabled = !isChecked && !canPinMore;
    const isEditorCheckBoxDisabled = isEditingWorkspace && this.isCheckedParentTagOfUncheckedTag(tag);

    return (
      <TooltipCoordinator render={() => maybeRenderItemTooltip(canPinMore)}>
        <TagDropdownItem
          tag={tag}
          tagsPath={tagsPath}
          key={tag.id}
          index={index}
          isChecked={isChecked}
          nestingLevel={nestingLevel}
          onSelect={onTagSelect}
          isDisabled={isDisabled}
          isEditingWorkspace={isEditingWorkspace}
          renderRightContent={() => (
            <DropdownItemCheckbox isChecked={isChecked} isDisabled={isEditorCheckBoxDisabled} />
          )}
        />
      </TooltipCoordinator>
    );
  };

  private readonly renderEmpty = () => {
    const {onCreateTagSelect, shouldDisplayAddButton} = this.props;

    // If there's already the option to create a tag in the dropdown, also give the option for empty state.
    if (shouldDisplayAddButton) {
      return (
        <DropdownEmpty>
          <StyledEmptyStateText>
            <Trans id="picktags-dropdown-noresults">No results</Trans>
          </StyledEmptyStateText>
          <StyledInlineLinkButton onClick={() => this.onAddClick(this.state.searchQuery)}>
            <StyledInlineLinkText>
              <Trans id="picktags-dropdown-auto-create-tag">
                Create tag &quot;{this.state.searchQuery}&quot;
              </Trans>
            </StyledInlineLinkText>
          </StyledInlineLinkButton>
        </DropdownEmpty>
      );
    }

    if (!onCreateTagSelect) {
      return null;
    }

    return (
      <DropdownVirtualList
        key={this.state.searchQuery}
        width={300}
        maxHeight={385}
        rowCount={2}
        renderRow={(index) => this.renderFixedEmptyRow(index, this.state.searchQuery, onCreateTagSelect)}
        onRequestClose={this.props.onRequestClose}
      />
    );
  };

  private readonly renderFixedEmptyRow = (
    index: number,
    query: string,
    onCreateTagSelect: (e: FrontEvent, initialName: string) => void,
  ) => {
    switch (index) {
      case 0:
        return (
          <DropdownCategory>
            <Trans id="dropdown-tags-section-create">Create</Trans>
          </DropdownCategory>
        );

      case 1:
        return (
          <DropdownItem onSelect={(e) => onCreateTagSelect(e, query)} index={0}>
            <DropdownItemAccentIcon label={null} iconName="dropdownNewTag" />
            <DropdownItemContent>
              <Trans id="dropdown-tags-tagnamed">Tag &#34;{query}&#34;</Trans>
            </DropdownItemContent>
          </DropdownItem>
        );

      default:
        return null;
    }
  };

  /*
   * Event handlers.
   */

  private readonly onAddClick = (searchQuery?: string) => {
    this.setState({activeItem: PickTagsActiveItemsEnum.CREATE_TAGS});
    if (searchQuery) {
      this.setState({searchQuery});
    }
  };

  /*
   * Render.
   */

  render() {
    const {isEditingWorkspace} = this.props;

    if (this.state.activeItem === PickTagsActiveItemsEnum.CREATE_TAGS) {
      return (
        <CreateWorkspaceTagDropdownContainer
          {...this.props}
          backButtonLabel={undefined}
          onBackButtonClick={noop}
          initialNamespace={this.props.namespace}
          onTagCreated={this.props.onTagCreated}
          shouldHideShowInWorkspace={isEditingWorkspace}
          isEditingWorkspace={isEditingWorkspace}
          // auto-populate tag name to be search query.
          initialName={this.state.searchQuery}
        />
      );
    }

    return (
      <AsyncSearchDropdown
        {...this.props}
        headerTitle={this.renderTitle()}
        headerRightIcon={this.maybeRenderAddButton()}
        query={this.state.searchQuery}
        onQueryChange={(newQuery) => this.setState({searchQuery: newQuery})}
        onRequestData={this.fetchAsync}
        renderRow={this.renderRow}
        renderEmpty={this.renderEmpty}
        height={workspacesEditorDropdownHeight}
      />
    );
  }

  private renderTitle() {
    const {team, titleLabel} = this.props;

    // If no team was provided, these are private tags.
    if (!team) {
      return titleLabel || <Trans id="workspace-privatetags">Private tags</Trans>;
    }

    // Otherwise, display the team avatar with team label.
    const teamLabel = titleLabel || <>{team.name} tags</>;
    return (
      <StyledHeaderDiv>
        <TeamAvatar team={team} size={VisualSizesEnum.SMALL} />
        <TooltipCoordinator render={() => teamLabel}>
          <StyledTeamNameDiv>
            <TooltipOverflowCondition>{teamLabel}</TooltipOverflowCondition>
          </StyledTeamNameDiv>
        </TooltipCoordinator>
      </StyledHeaderDiv>
    );
  }

  private maybeRenderAddButton() {
    if (!this.props.shouldDisplayAddButton) {
      return null;
    }
    return (
      <UserThemeConsumer>
        {(theme) => (
          <IconButton
            iconName="plusCircle"
            onClick={() => this.onAddClick()}
            label={<Trans id="pick-tags-create-new-tag">Create new tag</Trans>}
            hoverColor={theme.greys.shade80}
            color={theme.greys.shade70}
          />
        )}
      </UserThemeConsumer>
    );
  }
}

function maybeRenderItemTooltip(canPinMore: boolean) {
  if (canPinMore) {
    return null;
  }

  return (
    <Plural
      id="workspace-privatetags-limit"
      value={visibleCustomViewIdsMaxLength}
      one="You cannot pin more than 1 tag"
      other="You cannot pin more than # tags"
    />
  );
}
